import React from 'react';
import { Check, Plus } from 'lucide-react';
import { SetAsideType } from '../../types';
import { SET_ASIDES } from '../../lib/constants';
import { SetAsideIcon } from '../SetAsideIcon';

interface SetAsideSelectorProps {
  selectedSetAsides: SetAsideType[];
  onChange: (setAsides: SetAsideType[]) => void;
}

export function SetAsideSelector({ selectedSetAsides, onChange }: SetAsideSelectorProps) {
  const handleToggle = (type: SetAsideType) => {
    if (selectedSetAsides.includes(type)) {
      onChange(selectedSetAsides.filter(t => t !== type));
    } else {
      onChange([...selectedSetAsides, type]);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-bw-ink">Set-Aside &amp; Eligibility</h3>
        <p className="text-sm text-bw-muted mt-0.5">
          Establish what your business qualifies for — this matches you with set-aside opportunities and grounds eligibility claims in proposals.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {Object.values(SET_ASIDES).map(setAside => {
          const selected = selectedSetAsides.includes(setAside.type);
          return (
            <div
              key={setAside.type}
              role="checkbox"
              aria-checked={selected}
              tabIndex={0}
              onClick={() => handleToggle(setAside.type)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleToggle(setAside.type);
                }
              }}
              className={`relative flex items-start gap-3 p-3.5 rounded-xl border-[1.5px] cursor-pointer transition-colors ${
                selected
                  ? 'border-success-border bg-success-bg'
                  : 'border-bw-border bg-bw-surface hover:border-bw-primary/40'
              }`}
            >
              {/* Verified / unset indicator */}
              <span
                className={`flex-shrink-0 mt-0.5 w-5 h-5 rounded-full flex items-center justify-center ${
                  selected ? 'bg-success text-white' : 'border-[1.5px] border-bw-border text-bw-muted'
                }`}
              >
                {selected ? <Check className="h-3 w-3" /> : <Plus className="h-3 w-3" />}
              </span>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <SetAsideIcon type={setAside.type} showTooltip={false} />
                  <span className={`text-sm font-semibold ${selected ? 'text-success' : 'text-bw-ink'}`}>
                    {setAside.name}
                  </span>
                </div>
                <p className="mt-1 text-sm text-bw-muted">{setAside.description}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
